import React, { useState, useRef } from 'react';
import { Search, X, MapPin, ChevronRight, Loader2 } from 'lucide-react';
import { searchWineries } from '@/lib/api';

let cachedWineries = null;

async function getAllWineries() {
  if (cachedWineries) return cachedWineries;
  cachedWineries = await searchWineries('');
  return cachedWineries;
}

export default function WinerySearch({ onSelectWinery }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceRef = useRef(null);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.trim().length < 2) {
      setResults([]);
      setHasSearched(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const all = await getAllWineries();
        const normalize = s => s.toLowerCase().replace(/[‘’‚‛]/g, "'");
        const lower = normalize(val.trim());
        const filtered = all.filter(w =>
          normalize(w.name ?? '').includes(lower) ||
          normalize(w.region ?? '').includes(lower)
        );
        setResults(filtered.slice(0, 10));
        setHasSearched(true);
      } catch {
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 350);
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setHasSearched(false);
  };

  return (
    <div>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <input
          type="text"
          value={query}
          onChange={handleChange}
          placeholder="Type a winery name..."
          className="w-full pl-10 pr-10 py-3 rounded-xl border border-stone-300 bg-stone-50 text-stone-800 text-sm focus:outline-none focus:border-[#722F37] focus:ring-1 focus:ring-[#722F37]"
        />
        {query && (
          <button onClick={handleClear} className="absolute right-3 top-1/2 -translate-y-1/2">
            <X className="w-4 h-4 text-stone-400" />
          </button>
        )}
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-[#722F37]" />
        </div>
      )}

      {!isLoading && hasSearched && results.length === 0 && (
        <p className="text-sm text-stone-500 text-center py-4">No wineries found</p>
      )}

      {!isLoading && results.length > 0 && (
        <div className="mt-3 space-y-1.5 max-h-64 overflow-y-auto">
          {results.map(winery => (
            <button
              key={winery.id || winery.name}
              onClick={() => { onSelectWinery(winery); handleClear(); }}
              className="w-full flex items-center gap-3 p-3 rounded-xl bg-stone-50 hover:bg-stone-100 transition-colors text-left border border-stone-200"
            >
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-stone-800 truncate text-sm">{winery.name}</p>
                <div className="flex items-center gap-1 text-stone-500 text-xs mt-0.5">
                  <MapPin className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{winery.region || winery.address}</span>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-stone-400 flex-shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
